import { useEffect, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

type Props = {
  mode: "room" | "dm" | null;
  onClose: () => void;
  onCreated: (id: Id<"conversations">) => void;
};

export function NewConversationDialog({ mode, onClose, onCreated }: Props) {
  return (
    <Dialog open={mode !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        {mode === "room" ? (
          <NewRoomForm onCreated={onCreated} />
        ) : mode === "dm" ? (
          <NewDmForm onCreated={onCreated} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function NewRoomForm({
  onCreated,
}: {
  onCreated: (id: Id<"conversations">) => void;
}) {
  const [name, setName] = useState("");
  const [pending, setPending] = useState(false);
  const createRoom = useMutation(api.conversations.createRoom);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setPending(true);
    try {
      const id = await createRoom({ name });
      onCreated(id);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setPending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <DialogHeader>
        <DialogTitle>New room</DialogTitle>
        <DialogDescription>
          Anyone signed in can be added as a member.
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-2">
        <Label htmlFor="room-name">Name</Label>
        <Input
          id="room-name"
          value={name}
          autoFocus
          maxLength={64}
          onChange={(e) => setName(e.target.value)}
          placeholder="general"
        />
      </div>
      <DialogFooter>
        <Button type="submit" disabled={pending || !name.trim()}>
          {pending ? <Loader2 className="size-4 animate-spin" /> : null}
          Create
        </Button>
      </DialogFooter>
    </form>
  );
}

function NewDmForm({
  onCreated,
}: {
  onCreated: (id: Id<"conversations">) => void;
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [pending, setPending] = useState(false);
  const results = useQuery(api.users.search, debounced ? { query: debounced } : "skip");
  const getOrCreateDm = useMutation(api.conversations.getOrCreateDm);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  const start = async (otherUserId: Id<"users">) => {
    setPending(true);
    try {
      const id = await getOrCreateDm({ otherUserId });
      onCreated(id);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle>New direct message</DialogTitle>
        <DialogDescription>Search by name or email.</DialogDescription>
      </DialogHeader>
      <Input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search users..."
      />
      <div className="max-h-72 overflow-y-auto -mx-1 space-y-1">
        {results === undefined && debounced ? (
          <p className="text-sm text-muted-foreground px-1">Searching...</p>
        ) : null}
        {results && results.length === 0 && debounced ? (
          <p className="text-sm text-muted-foreground px-1">No users found.</p>
        ) : null}
        {results?.map((u) => {
          const initials = (u.name ?? u.email ?? "?")
            .split(" ")
            .map((s) => s[0])
            .join("")
            .slice(0, 2)
            .toUpperCase();
          return (
            <button
              key={u._id}
              disabled={pending}
              onClick={() => start(u._id)}
              className="w-full flex items-center gap-3 rounded-md p-2 hover:bg-accent text-left disabled:opacity-50"
            >
              <Avatar className="size-8">
                {u.image ? <AvatarImage src={u.image} /> : null}
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {u.name ?? "Unnamed"}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {u.email}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
